// @ts-check

import { Component } from '@theme/component';
import { CustomizerOpenEvent, CustomizerUpdateEvent, ThemeEvents } from '@theme/events';
import { CustomizerStore } from '@theme/custom.customizer-store';
import {
  addOnLines,
  carryAddOns,
  changeCartLine,
  editedCartLine,
  editLineKey,
  fetchCart,
  fetchCartLine,
  refreshCartSections,
  removeCartLine,
  replacementLine,
  restoreCartLine,
} from '@theme/custom.customizer-cart-edit';
import { ADDON_ID_PROPERTY } from '@theme/custom.cart-addon-links';
import {
  canExpandComponents,
  expandOn,
  plannedComponents,
  syncDesignLines,
} from '@theme/custom.customizer-components';
import { readIsland, resolveConfigDoc } from '@theme/custom.customizer-resolve';
import { CustomizerAdapter } from '@theme/custom.customizer-adapter';
import { MetalColors } from '@theme/custom.metal-colors';
import { renderProofFiles } from '@theme/custom.customizer-proof';
import {
  CustomizerCommitCoordinator,
  captureOrder,
  fitsCartAdd,
} from '@theme/custom.customizer-commit';
import { ProofDraft } from '@theme/custom.customizer-proof-draft';

/** @typedef {import('@theme/custom.customizer-drill').DrillAdvance} DrillAdvance */
/** @typedef {import('@theme/custom.metal-colors').MetalColorRecord} MetalColorRecord */
/** @typedef {import('@theme/custom.customizer-cart-edit').CartLine} CartLine */
/** @typedef {import('@theme/custom.customizer-commit').CartFailure} CartFailure */
/**
 * @typedef {object} CartReplacement
 * @property {string | null} key
 * @property {CartLine | null} line
 * @property {string} config
 * @property {string | null} rowsName
 * @property {() => boolean} unchanged
 * @property {boolean} [addonsReplayed]
 * @property {{ original: CartLine, replacement: CartLine }} [removal]
 */

/**
 * A component honoring the drill protocol, as this host consumes it.
 * @typedef {{ toRoot: () => void, advanceIntent?: () => DrillAdvance | null }} ActiveDrill
 */

/** How long Review has to sit unchanged before the order frames start drawing, so the hero's own
 *  Review render goes first. */
const PROOF_DRAW_DELAY = 300;

/** Names the product option a landing page already asked (sections/custom-start-designing.liquid),
 *  so the wizard opens on the step after it. */
const START_AFTER_PARAM = 'customizer_after';

/**
 * The hand-off hooks the overlay shell exposes (assets/custom.customizer-overlay.js). All optional:
 * a customizer can render outside the overlay, and then there is no dialog to choreograph.
 * @typedef {{
 *   beginCommit?: () => void,
 *   cancelCommit?: () => void,
 *   completeCommit?: (intoDrawer: boolean) => Promise<void>,
 * }} CustomizerOverlayCommit
 */

/**
 * The footer hooks the step shell exposes (assets/custom.customizer-step.js): it owns the CTA's
 * surroundings, so it owns the add-to-cart error line.
 * @typedef {{
 *   showCommitError?: (message?: string, reason?: CartFailure) => void,
 *   clearCommitError?: () => void,
 * }} CustomizerStepCommit
 */

/**
 * @typedef {object} CustomizerRefs
 * @property {HTMLElement} [incompleteDialog] - The add-to-cart blocking dialog; optional because
 *   it only renders inside the full overlay markup.
 *
 * @extends Component<CustomizerRefs>
 */
class CustomizerComponent extends Component {
  /**
   * The context-layer store. Starts empty so it is always a valid store and satisfies strict
   * property initialization; descendants read it via `closest('custom-customizer').store`.
   * @type {CustomizerStore}
   */
  store = new CustomizerStore();

  /**
   * The cart line this visit is editing, or null on an ordinary product-page visit. It authorizes
   * a cart DELETION on finish, so it must never outlive the one add it belongs to.
   * @type {string | null}
   */
  #editLineKey = editLineKey();

  /**
   * The edited line as /cart.js reported it at restore time. Lives and dies with
   * {@link #editLineKey}.
   * @type {import('@theme/custom.customizer-cart-edit').CartLine | null}
   */
  #editLine = null;

  /**
   * The photos {@link #restoreEditedLine} put back, File identity included, so
   * {@link #canUpdateInPlace} can tell an untouched photo from a re-upload.
   * @type {import('@theme/custom.customizer-cart-edit').RestoredUpload[]}
   */
  #restoredUploads = [];

  /** An accepted add retries only its cleanup, not the purchase. @type {CartReplacement | null} */
  #pendingReplacement = null;

  /** @type {string | null} */
  #rowsName = null;

  /** The resolved doc the store was built from - `#configPayload` asks it whose measurement places
   * each face. @type {import('@theme/custom.customizer-resolve').ResolvedConfig['doc']} */
  #resolvedDoc = null;

  /** Lazy adapter for the placement-provenance question only (the photo editor holds its own the
   * same way); rendering stays the preview's. @type {CustomizerAdapter | null} */
  #placementAdapter = null;

  /**
   * The proof frames for the add being assembled, or null while they still have to be rendered.
   * `finish()` re-enters once they land, which is what keeps the submit itself synchronous.
   * @type {Array<{ name: string, file: File }> | null}
   */
  #proofFiles = null;

  /**
   * The faces of that same attempt that owed frames and could not produce usable ones. Rides
   * `_config` so a face whose photo did not draw is stated on the order rather than merely absent.
   * @type {string[]}
   */
  #proofUnrendered = [];

  #commitCoordinator = new CustomizerCommitCoordinator();

  /** A completed cart mutation must still reach the page if its host left during the request. */
  #cartEvents = Object.assign(new EventTarget(), {
    /** @param {Event} event */
    dispatchEvent: (event) => (this.isConnected ? this : document).dispatchEvent(event),
  });

  /** The order frames, drawn on Review and reused by the add while the design still matches. */
  #proofs = new ProofDraft((store, signal) =>
    renderProofFiles(store, readIsland(this, 'assets') ?? {}, this.#previewRenderers, signal)
  );

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #proofTimer;

  /**
   * The `_config` payload: the store's record plus what the store cannot know - the faces whose
   * engraving placement is a borrowed template's measurement, and the faces whose
   * frames could not be rendered at all. Support and the bench read it off the order; provenance
   * must never block an add, hence the swallow-and-say guard.
   * @param {string[]} [unrendered] - Faces that owed order frames and produced none
   *   ({@link renderProofFiles}). Their raw upload is on the order; their Proof and Crop are not.
   * @returns {string}
   */
  #configPayload(unrendered) {
    const config = this.store.cartConfig();
    this.#rowsName = null;
    if (unrendered?.length) config.unrenderedFaces = [...unrendered];
    // The parts this design ships, from the product's own config (the one the pricing projection
    // is derived from), never from a doc the page has since adjusted.
    try {
      const assets =
        /** @type {{ stoneVariants?: Record<string, number>, componentIds?: unknown } | undefined} */ (
          readIsland(this, 'assets')
        );
      const components = plannedComponents(
        config,
        /** @type {any} */ (readIsland(this, 'config')),
        assets?.stoneVariants
      );
      if (components.length) config.components = components;
      if (expandOn() && canExpandComponents(components, assets?.componentIds)) {
        this.#rowsName = crypto.randomUUID();
      }
    } catch (error) {
      console.warn('[custom-customizer] component plan skipped:', error);
    }
    try {
      const doc = /** @type {any} */ (this.#resolvedDoc);
      if (doc?.borrowedGeometry) {
        this.#placementAdapter ??= new CustomizerAdapter({
          assets: readIsland(this, 'assets') ?? {},
        });
        const borrowed = this.#placementAdapter.borrowedPlacements(
          this.store,
          /** @type {any} */ (doc)
        );
        if (borrowed.length) config.borrowedPlacements = borrowed;
      }
    } catch (error) {
      console.warn('[custom-customizer] placement provenance skipped:', error);
    }
    return JSON.stringify(config);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#buildStore();

    // Closing ends the timeout grace period; a completed visit resets after the overlay exits.
    this.#listeners = new AbortController();
    this.closest('dialog')?.addEventListener(
      'close',
      () => {
        this.#commitCoordinator.disarmLateSettle();
        if (this.#resetOnClose) this.#buildStore();
        this.#resetOnClose = false;
      },
      { signal: this.#listeners.signal }
    );

    // Bound to the OPEN event rather than done here: a variant picked after page load (a swatch
    // click) has to win over whatever was selected when this element upgraded.
    document.addEventListener(ThemeEvents.customizerOpen, this.#adoptPdpVariant, {
      signal: this.#listeners.signal,
    });

    // The overlay stands down from its own auto-open on this path and waits for the event
    // #restoreEditedLine dispatches, so no empty wizard flashes before the saved configuration.
    if (this.#editLineKey) void this.#restoreEditedLine();
    else this.#startAfterLanding();
  }

  /**
   * Open past the option a landing page already asked, on the variant it linked. Adopted here, not
   * on open: the overlay's `?customizer=open` auto-open calls itself and dispatches no open event.
   */
  #startAfterLanding() {
    const url = new URL(window.location.href);
    const option = url.searchParams.get(START_AFTER_PARAM) ?? '';
    if (option === '') return;
    // One-shot: a reload after the shopper walked back must not skip the step again.
    url.searchParams.delete(START_AFTER_PARAM);
    history.replaceState(history.state, '', url);
    this.#adoptPdpVariant(new CustomizerOpenEvent(this.dataset.configId ?? ''));
    if (this.#adoptedVariantId !== null) this.store.startAfter(option);
  }

  /**
   * Listeners held for as long as this element is in the page. Replaced on every connect, so an
   * element the Section Rendering API re-inserts re-arms instead of registering onto a dead signal.
   * @type {AbortController}
   */
  #listeners = new AbortController();

  /**
   * A committed add is waiting on the overlay's close to start the wizard over. Set by
   * {@link #handoffToCart}, spent by the `close` listener in {@link connectedCallback}.
   * @type {boolean}
   */
  #resetOnClose = false;

  /**
   * The product-page variant the wizard was last opened on. Null until the first open and whenever
   * the store is rebuilt, both of which mean "adopt whatever is selected out there".
   * @type {string | null}
   */
  #adoptedVariantId = null;

  /**
   * Adopt only a newer PDP variant: wizard picks survive reopening, later PDP picks win.
   * Adoption rewrites axes only, preserving engraving, photo, and monogram inputs.
   * @param {Event} event - A `CustomizerOpenEvent` carrying `{ configId }`.
   */
  #adoptPdpVariant = (event) => {
    const { configId } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (configId && configId !== this.dataset.configId) return;
    // Edited lines own their committed axes; #restoreEditedLine has already restored them.
    if (this.#editLineKey) return;

    const input = document
      .querySelector('product-form-component form[data-type="add-to-cart-form"]')
      ?.querySelector('input[name="id"]');
    // Checked options are the visible, immediate PDP pick; URL and input can lag or be absent.
    // This also survives variant-matrix holes that remove the variant parameter.
    const wantedFromPage = this.store.variantIdForValues(this.#checkedOptionValues());
    // When the PDP quotes a floor but exposes no choice, open on the cheapest variant it promised.
    const wantedFromFloor =
      wantedFromPage === null && this.#pageQuotedAFloor() ? this.store.cheapestVariantId() : null;
    // Then use URL and input; the server-rendered input can lag a swatch press.
    // `adoptVariant` rejects missing or cross-product parameters.
    const candidates = [
      wantedFromPage === null ? '' : String(wantedFromPage),
      new URL(window.location.href).searchParams.get('variant') ?? '',
      wantedFromFloor === null ? '' : String(wantedFromFloor),
      input instanceof HTMLInputElement ? input.value : '',
    ];

    for (const wanted of candidates) {
      if (wanted === '') continue;
      if (wanted === this.#adoptedVariantId) return;
      if (this.store.adoptVariant(wanted)) {
        this.#adoptedVariantId = wanted;
        return;
      }
    }
  };

  /**
   * The option values the shopper can actually see chosen in the product page's own variant
   * picker, e.g. `['Sterling Silver']`. Scoped to the picker stock marks as this template's
   * product, so a product card or quick-add elsewhere on the page cannot answer for the PDP.
   *
   * A wizard-owned axis is skipped. variant-main-picker.liquid keeps those rendered and checked,
   * hiding them only in CSS, so stock's own Section Rendering request stays byte-identical; but
   * they sit on the loaded variant's value, which the shopper can neither see nor change. Reading
   * one would constrain a metal pick by a size they never chose.
   * @returns {Array<{ position: number, value: string }>} Visible product option picks.
   */
  #checkedOptionValues() {
    const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!picker) return [];
    /** @type {Array<{ position: number, value: string }>} */
    const values = [];
    for (const field of picker.querySelectorAll('input[type="radio"]:checked, select')) {
      const row = field.closest('.variant-option');
      if (!row || row.classList.contains('variant-option--wizard-owned')) continue;
      const position = Number(/** @type {HTMLElement} */ (row).dataset.optionIndex);
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
        if (field.value && Number.isInteger(position) && position >= 0) {
          values.push({ position, value: field.value });
        }
      }
    }
    return values;
  }

  /**
   * Did the product page quote a floor rather than one variant's price? `data-label-from` carries
   * the "From" prefix custom.customizer-step.liquid sets from custom.customizer-from-price, and is
   * empty on every product whose page shows an exact number.
   * @returns {boolean}
   */
  #pageQuotedAFloor() {
    return (
      (this.querySelector('custom-customizer-step')?.getAttribute('data-label-from') ?? '') !== ''
    );
  }

  /** Rebuild the store as a FRESH instance, not a field-by-field clear, so no forgotten field leaks
   *  into the next config; safe since children reach it live via a `host?.store` getter. */
  #buildStore() {
    this.#detachCommit();
    this.#pendingReplacement = null;
    this.#editLine = null;
    this.#restoredUploads = [];
    delete this.dataset.editing;
    this.#proofFiles = null;
    this.#proofUnrendered = [];
    this.#overlay?.cancelCommit?.();
    this.store.dispose();
    const { doc, variants, options } = resolveConfigDoc(this);
    const assets = /** @type {{ metalColors?: MetalColorRecord[] } | undefined} */ (
      readIsland(this, 'assets')
    );
    this.store = new CustomizerStore(
      doc,
      variants,
      this.dataset.metalOrder,
      this.dataset.requestProof === 'true',
      options,
      new MetalColors(assets?.metalColors)
    );
    this.#resolvedDoc = this.store.configSchema;
    this.#placementAdapter = null;
    this.#proofs.clear();
    this.store.subscribe(this.#broadcast);
    // A fresh store holds no axes, so the next open has to adopt again even though the product page
    // has not changed. Without this the wizard would reopen after an add with no metal selected.
    this.#adoptedVariantId = null;
    this.#broadcast();
  }

  /**
   * Restore the URL's line for this visit. An unreadable line opens an empty wizard and drops
   * the edit key, so finishing deletes nothing; a replaced visit accepts no restore or open event.
   * @returns {Promise<void>}
   */
  async #restoreEditedLine() {
    const key = this.#editLineKey;
    if (!key) return;
    const store = this.store;
    const { signal } = this.#listeners;
    const isCurrent = () => this.isConnected && this.store === store && !signal.aborted;

    const line = await fetchCartLine(key);
    if (!isCurrent()) return;
    const restored = line ? await restoreCartLine(store, line) : null;
    if (!isCurrent()) return;
    if (!line || !restored?.ok) {
      this.#editLineKey = null;
    } else {
      this.#editLine = line;
      this.#restoredUploads = restored.uploads;
      // Set before the broadcast so the step footer's edit labels land in the same repaint: the
      // hydrate's own broadcasts have already run by now.
      this.dataset.editing = '';
      this.#broadcast();
    }

    // The two elements upgrade off separate module scripts, so an open dispatched into that gap is
    // dropped silently. `whenDefined` resolves only after the upgrade has run connectedCallback.
    await customElements.whenDefined('custom-customizer-overlay');
    if (!isCurrent()) return;
    this.dispatchEvent(new CustomizerOpenEvent(this.dataset.configId ?? ''));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detachCommit();
    this.#proofFiles = null;
    this.#proofUnrendered = [];
    this.#overlay?.cancelCommit?.();
    this.store.dispose();
    this.#listeners.abort();
    clearTimeout(this.#proofTimer);
    this.#proofs.clear();
  }

  /** Only submitted cart cleanup survives a visit; its callbacks cannot control a new overlay. */
  #detachCommit() {
    this.#commitCoordinator.detach();
    this.#commitCoordinator = new CustomizerCommitCoordinator();
  }

  #broadcast = () => {
    this.dispatchEvent(new CustomizerUpdateEvent(this.store, this.dataset.configId ?? ''));
    this.#drawProofsOnReview();
  };

  /** Start the order frames once Review has settled, so the add only has to upload them. */
  #drawProofsOnReview() {
    clearTimeout(this.#proofTimer);
    if (!this.store.isReviewStep) return;
    this.#proofTimer = setTimeout(() => {
      const store = this.store;
      if (!this.isConnected || !store.isReviewStep || store.firstBlockingStep()) return;
      this.#proofs.prepare(store);
    }, PROOF_DRAW_DELAY);
  }

  next() {
    this.store.next();
  }

  /** Footer CTA: finish into the cart on the final step, otherwise advance. Bound via
   *  `on:click="/advance"`: the leading slash is required, a bare name silently no-ops. */
  advance() {
    // The footer's CTA disable is aria-disabled + CSS only, so the button stays keyboard-focusable
    // and Enter still lands here. Re-check the gates, never trust the visual disable.
    if (!this.store.canAdvance) return;
    // A drilled view's CTA claim runs ahead of the gate below, since its own writes clear it.
    // A claim that re-enters here for a plain advance must take the plain path, hence #claiming.
    const drilled = this.#claiming ? null : this.drillAdvance;
    if (drilled) {
      this.#claiming = true;
      try {
        drilled.run();
      } finally {
        this.#claiming = false;
      }
      return;
    }
    if (this.store.currentStepIncomplete()) return;
    if (this.store.isLastStep) void this.finish();
    else this.store.next();
  }

  /** Finish into the cart via the page's native buy-buttons form, so Horizon's own /cart/add.js
   *  flow runs; the surcharge is display only, the Cart Transform app bills the real price.
   *  The overlay holds, CTA busy, until the cart answers: closing on click would race the add and
   *  strand a failure's error text on the PDP, behind the overlay. */
  async finish() {
    // One add at a time. The CTA is pointer-inert while committing (CSS), but the disable is
    // aria-only so Enter still reaches advance(), and a double add charges the shopper twice.
    if (this.#commitCoordinator.busy) return;
    if (this.#editLineKey && !this.#editLine) return;
    if (this.#pendingReplacement) {
      void this.#acceptAdd(this.store, this.#pendingReplacement);
      return;
    }

    // The per-step Next gate only sees the CURRENT step, so a required face emptied after it was
    // completed (upload, advance, come back, Remove Photo) would sail through Review.
    const blocking = this.store.firstBlockingStep();
    if (blocking) {
      // Duck-typed because the dialog class is registered by its own module: an un-upgraded
      // element still routes the shopper to the blocking step.
      const dialog = this.refs.incompleteDialog;
      if (dialog && 'show' in dialog && typeof dialog.show === 'function') {
        dialog.show(blocking.index, blocking.label);
      } else {
        this.store.goTo(blocking.index);
      }
      return;
    }

    // The frames the shopper just approved, usually drawn already on Review. Re-entrant rather than
    // awaited inline: the submit stays synchronous and the gates above stay first.
    if (this.#proofFiles === null) {
      const source = this.store;
      const draft = this.#proofs.draw(source);
      this.#step?.clearCommitError?.();
      // The CTA reads busy through a render still running: seconds of canvas work on a phone.
      this.#overlay?.beginCommit?.();
      void this.#commitCoordinator.prepareProofs(source, {
        // Leaving Review stops the draw with the design unchanged; that must not ship frameless.
        isCurrent: () => this.isConnected && this.store === source && draft.current(),
        render: () => draft.frames,
        onReady: ({ files, unrendered }) => {
          this.#proofFiles = files;
          this.#proofUnrendered = unrendered;
          void this.finish();
        },
        onError: (error) => {
          if (draft.current()) console.warn('[custom-customizer] proof frames skipped:', error);
        },
        onInvalid: () => {
          this.#proofFiles = null;
          this.#proofUnrendered = [];
          this.#overlay?.cancelCommit?.();
        },
      });
      return;
    }
    const proofs = this.#proofFiles;
    const unrendered = this.#proofUnrendered;
    // Consume frames per attempt so a retry cannot ship a design the shopper revised.
    this.#proofFiles = null;
    this.#proofUnrendered = [];

    const source = this.store;
    const orderUnchanged = captureOrder(source);
    const unchanged = () => this.store === source && orderUnchanged();
    const config = this.#configPayload(unrendered);
    /** @type {import('@theme/custom.customizer-cart-edit').Cart | null} */
    let cart = null;

    if (this.#editLineKey) {
      const controller = this.#commitCoordinator.beginChange();
      try {
        cart = await fetchCart(controller.signal);
        if (!this.#commitCoordinator.isCurrent(controller)) return;
        controller.signal.throwIfAborted();
        const current = editedCartLine(cart?.items ?? [], this.#editLine, true);
        if (!current || !unchanged()) {
          this.#failCommit(undefined, current ? undefined : 'cart-changed');
          return;
        }
        this.#editLine = current;
        this.#editLineKey = current.key;
        this.#endCommit();
      } catch {
        if (this.#commitCoordinator.isCurrent(controller)) this.#failCommit();
        return;
      }
    }

    // In-place updates cannot carry new files or a variant switch; add-then-remove handles those.
    // Revised proof frames also require re-upload; identical `_config` retains existing frames.
    const designUnchanged = this.#editLine?.properties?._config === config;
    // Existing lines without proof frames must take the add path: change.js cannot upload them.
    const framesOnLine = proofs.every(
      ({ name }) => typeof this.#editLine?.properties?.[name] === 'string'
    );
    // A face that produced no frames must not take the in-place path: change.js would keep the
    // line's old frames while the new `_config` says that face produced none.
    if (
      cart &&
      this.#editLineKey &&
      this.#canUpdateInPlace() &&
      !unrendered.length &&
      (!proofs.length || (designUnchanged && framesOnLine))
    ) {
      void this.#updateEditedLine(config, proofs, cart, unchanged);
      return;
    }

    const form = /** @type {HTMLFormElement | null} */ (
      document.querySelector('product-form-component form[data-type="add-to-cart-form"]')
    );
    if (!form) {
      // TODO: fleet fallback, if a template has no native product form, POST FormData to
      // Theme.routes.cart_add_url and dispatch cart:update instead.
      console.warn('[custom-customizer] no product form found to finish into');
      // The proof pass already put the overlay in its committing state; nothing below will end it.
      this.#overlay?.cancelCommit?.();
      return;
    }

    const variantId = this.store.selectedVariant?.id;
    const idInput = form.querySelector('[name="id"]');
    if (variantId && idInput instanceof HTMLInputElement) idInput.value = String(variantId);

    // Rebuild only our injected inputs; leave the theme's own form fields untouched.
    for (const stale of form.querySelectorAll('[data-customizer-prop]')) stale.remove();
    /** @type {Record<string, string>} */
    const props = this.store.cartProperties();
    props._config = config;
    if (this.#rowsName) props._rows = this.#rowsName;
    // A bundle's add-ons link to it by this id; the replacement line inherits it so they follow.
    const addonId = this.#editLine?.properties?.[ADDON_ID_PROPERTY];
    if (typeof addonId === 'string' && addonId !== '') props[ADDON_ID_PROPERTY] = addonId;
    const fileParts = [...this.store.cartFileProperties(), ...proofs];
    if (!fitsCartAdd(props, fileParts)) {
      this.#failCommit(undefined, 'too-large');
      return;
    }
    for (const [key, value] of Object.entries(props)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value;
      input.dataset.customizerProp = '1';
      form.appendChild(input);
    }

    // A real file input (populated via DataTransfer) is what makes FormData carry the photo as a
    // file part, so /cart/add hosts it: the hidden-input path above only carries strings.
    let hasFileParts = false;
    for (const { name, file } of fileParts) {
      const input = document.createElement('input');
      input.type = 'file';
      input.name = `properties[${name}]`;
      input.hidden = true;
      input.dataset.customizerProp = '1';
      const transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
      form.appendChild(input);
      hasFileParts = true;
    }

    this.#step?.clearCommitError?.();
    this.#overlay?.beginCommit?.();
    // Wired before the submit, not after: the answer comes back on the document bus, and the
    // committing state must always have something able to end it.
    const requestId = crypto.randomUUID();
    form.dataset.customizerRequestId = requestId;
    const replacement = {
      key: this.#editLineKey,
      line: this.#editLine,
      config,
      rowsName: props._rows ?? null,
      unchanged,
    };
    const isCurrent = () => this.isConnected && this.store === source;
    this.#commitCoordinator.watchCartResult({
      target: document,
      form,
      hasFileParts,
      requestId,
      onSuccess: (cart) => {
        void this.#acceptAdd(source, replacement, cart);
      },
      onFailure: (message, reason) => {
        if (isCurrent()) this.#failCommit(message, reason);
      },
      canSettleLate: () => Boolean(replacement.key),
      onLateSuccess: (cart) => {
        void this.#acceptAdd(source, replacement, cart, false);
      },
    });

    // Native submission captures FormData synchronously. The edited quantity belongs to that
    // request only, including when it is one; restore the PDP input before another purchase.
    const editedQuantity = this.#editLineKey ? this.#editLine?.quantity : undefined;
    const existing = form.elements.namedItem('quantity');
    const original = existing instanceof HTMLInputElement ? existing : null;
    const quantity = editedQuantity ? (original ?? document.createElement('input')) : null;
    const previous = original?.value ?? '';
    if (quantity) {
      quantity.value = String(editedQuantity);
      if (!original) {
        quantity.type = 'hidden';
        quantity.name = 'quantity';
        form.appendChild(quantity);
      }
    }
    try {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    } finally {
      if (original && quantity) original.value = previous;
      else quantity?.remove();
    }
  }

  /**
   * Whether the edited line can be rewritten via /cart/change.js. A variant switch disqualifies (a
   * line is pinned to its variant) and so does a changed photo, where "unchanged" means File
   * IDENTITY: the store still holds the very object the restore created.
   * @returns {boolean}
   */
  #canUpdateInPlace() {
    const line = this.#editLine;
    if (!line) return false;
    const variantId = this.store.selectedVariant?.id;
    if (variantId && line.variant_id && Number(variantId) !== Number(line.variant_id)) {
      return false;
    }
    return this.store
      .cartFileProperties()
      .every(({ file }) => this.#restoredUploads.some((upload) => upload.file === file));
  }

  /**
   * Rewrite the edited line in place. The answer comes from our own fetch, not the document bus,
   * so no listeners are wired. Unchanged photos re-submit the hosted URL the line already carries:
   * change.js replaces properties wholesale, and `cartProperties` skips `blob:` previews.
   * @param {string} config - The `_config` payload finish() already built.
   * @param {Array<{ name: string, file: File }>} proofs - The frames rendered for this attempt.
   *   Only their NAMES are used: the caller only routes here when the line's hosted frames still
   *   match the design, and wholesale property replacement would otherwise drop them.
   * @param {import('@theme/custom.customizer-cart-edit').Cart} cart - The guarded preflight.
   * @param {() => boolean} unchanged - The draft captured before that preflight.
   * @returns {Promise<void>}
   */
  async #updateEditedLine(config, proofs, cart, unchanged) {
    // An in-place retry must not race a late-settle armed by an earlier timed-out add - it could
    // spend #editLineKey mid-change.
    this.#commitCoordinator.disarmLateSettle();

    const line = /** @type {import('@theme/custom.customizer-cart-edit').CartLine} */ (
      this.#editLine
    );

    /** @type {Record<string, string>} */
    const props = this.store.cartProperties();
    props._config = config;
    for (const { name, file } of this.store.cartFileProperties()) {
      const upload = this.#restoredUploads.find((entry) => entry.file === file);
      if (!upload) continue;
      const url = line.properties?.[upload.propertyKey];
      if (typeof url === 'string') props[name] = url;
    }
    for (const { name } of proofs) {
      const url = line.properties?.[name];
      if (typeof url === 'string') props[name] = url;
    }
    // Same for the id the add-ons link a bundle by: dropped here, they would render as loose rows.
    const addonId = line.properties?.[ADDON_ID_PROPERTY];
    if (typeof addonId === 'string' && addonId !== '') props[ADDON_ID_PROPERTY] = addonId;

    this.#step?.clearCommitError?.();
    this.#overlay?.beginCommit?.();
    const controller = this.#commitCoordinator.beginChange();
    try {
      if (this.#rowsName) {
        const name = await this.#editedRowsName(line.properties, props, this.#rowsName);
        if (name) props._rows = name;
      }
      if (!this.#commitCoordinator.isCurrent(controller)) return;
      controller.signal.throwIfAborted();
      if (!unchanged()) {
        this.#failCommit();
        return;
      }
      const before = addOnLines(cart, line);
      const rewritten = await changeCartLine(
        line.key,
        line.quantity ?? 1,
        props,
        this.#cartEvents,
        controller.signal
      );
      // The write landed; its cart event may already have replaced this visit.
      void this.#carryAddOnsInPlace(before, rewritten, props);
      void syncDesignLines(this.#cartEvents);
      if (!this.#commitCoordinator.isCurrent(controller)) return;
      if (controller.signal.aborted) {
        this.#failCommit();
        return;
      }
    } catch (error) {
      if (!this.#commitCoordinator.isCurrent(controller)) return;
      // A failed change means the edit landed NOWHERE - unlike the removal, this must surface.
      // An abort has no shopper-readable message; the footer's generic copy covers it.
      const message =
        error instanceof Error && !controller.signal.aborted && error.message !== ''
          ? error.message
          : undefined;
      this.#failCommit(message);
      return;
    }

    this.#endCommit();
    this.#editLineKey = null;
    this.#editLine = null;
    delete this.dataset.editing;
    if (unchanged()) this.#handoffToCart();
    else this.#overlay?.cancelCommit?.();
  }

  /**
   * Swap the edited line out, then bring the cart's design lines up to date: the rows index, then
   * the chain and stone lines of any design that index does not hold (with the switch off, every
   * design).
   *
   * In that order: the replaced line's own component lines leave with it, and the new line's are
   * added against the cart as it reads afterwards.
   * @param {import('@theme/custom.customizer-cart-edit').Cart | undefined} cart
   * @param {CartReplacement} replacement
   *   - Identity captured at submission.
   * @returns {Promise<void>}
   */
  async #afterAdd(cart, replacement) {
    try {
      await this.#replaceEditedLine(cart, replacement);
    } finally {
      void syncDesignLines(this.#cartEvents);
    }
  }

  /**
   * @param {CustomizerStore} source
   * @param {CartReplacement} replacement
   * @param {import('@theme/custom.customizer-cart-edit').Cart} [cart]
   * @param {boolean} [handoff]
   */
  async #acceptAdd(source, replacement, cart, handoff = true) {
    const isCurrent = () => this.isConnected && this.store === source;
    if (!replacement.key) {
      void this.#afterAdd(cart, replacement).catch((error) => console.warn(error));
      if (isCurrent()) {
        if (handoff && replacement.unchanged()) this.#handoffToCart();
        else this.#overlay?.cancelCommit?.();
      }
      return;
    }
    if (isCurrent()) {
      this.#pendingReplacement = replacement;
      this.#commitCoordinator.beginChange();
      this.#overlay?.beginCommit?.();
    }
    try {
      await this.#afterAdd(cart, replacement);
    } catch (error) {
      console.warn('[custom-customizer] could not replace the edited line', error);
      if (isCurrent()) this.#failCommit(undefined, 'replacement-incomplete');
      return;
    }
    if (!isCurrent()) return;
    this.#endCommit();
    this.#pendingReplacement = null;
    this.#editLineKey = null;
    this.#editLine = null;
    delete this.dataset.editing;
    this.#step?.clearCommitError?.();
    if (handoff && replacement.unchanged()) this.#handoffToCart();
    else this.#overlay?.cancelCommit?.();
  }

  /**
   * Keep an unchanged design's rows name; a failed import falls back to ordinary nesting.
   * @param {Record<string, string> | null | undefined} before
   * @param {Record<string, string>} after
   * @param {string} fresh - Captured before loading the rows module can yield to a newer visit.
   * @returns {Promise<string | null>}
   */
  async #editedRowsName(before, after, fresh) {
    try {
      const { editedRowsName } = await import('@theme/custom.customizer-rows');
      return editedRowsName(before, after, fresh);
    } catch (error) {
      console.warn('[custom-customizer] could not name the edited design', error);
      return null;
    }
  }

  /**
   * Carry the add-ons an in-place rewrite stranded across to the rewritten line: the nested ones a
   * plain-to-bundle edit deleted, or the linked ones a bundle-to-plain edit left with no owner.
   * Best effort like the replacement path: a failure leaves loose rows, never a missing pendant.
   * @param {import('@theme/custom.customizer-cart-edit').AddOnLines} before - Read before the
   *   rewrite.
   * @param {import('@theme/custom.customizer-cart-edit').Cart} cart - As change.js returned it.
   * @param {Record<string, string>} props - The properties the rewrite submitted, which name the
   *   line under its new key.
   * @returns {Promise<void>}
   */
  async #carryAddOnsInPlace(before, cart, props) {
    if (before.native.length === 0 && before.linked.length === 0) return;
    try {
      const addonId = props[ADDON_ID_PROPERTY];
      const parent = cart?.items?.find(
        (item) =>
          item.properties?._config === props._config &&
          (!props._rows || item.properties?._rows === props._rows) &&
          (!addonId || item.properties?.[ADDON_ID_PROPERTY] === addonId)
      );
      if (!parent) throw new Error('the rewritten line is not in the cart');
      // The change already re-rendered the cart; only a replay that changed it needs another pass.
      if (await carryAddOns(before, parent, false)) await refreshCartSections(this.#cartEvents);
    } catch (error) {
      console.warn(
        '[custom-customizer] could not carry the add-on lines to the edited line',
        error
      );
    }
  }

  /**
   * Carry add-ons and remove only the line this accepted submission was replacing.
   * @param {import('@theme/custom.customizer-cart-edit').Cart | undefined} cart
   * @param {CartReplacement} replacement
   * @returns {Promise<void>}
   */
  async #replaceEditedLine(cart, replacement) {
    const { key, line, config, rowsName } = replacement;
    if (!key) return;

    const payload = cart?.items ? cart : await fetchCart(AbortSignal.timeout(8000));
    const original = editedCartLine(payload?.items ?? [], line);
    if (!original) {
      const attempted = replacement.removal;
      if (
        payload?.items &&
        attempted &&
        !payload.items.some(
          (item) =>
            item.key === attempted.original.key || editedCartLine([item], attempted.original, true)
        ) &&
        editedCartLine(payload.items, attempted.replacement)
      )
        return;
      throw new Error('Edited line missing or ambiguous');
    }
    const accepted = replacementLine(payload?.items ?? [], original.key, config, rowsName);
    if (!accepted) {
      throw new Error('Replacement missing');
    }

    // Before the removal, not after: Shopify deletes a nested line WITH its parent, so once the old
    // line is gone the gift bag and the protection plan are gone with it.
    if (!replacement.addonsReplayed) {
      replacement.addonsReplayed = true;
      await this.#replayAddOns(payload, original.key, config, rowsName);
    }
    replacement.removal = { original, replacement: accepted };
    if (!(await removeCartLine(original.key, this.#cartEvents, original))) {
      throw new Error('Original line removal failed');
    }
  }

  /**
   * Re-attach the edited line's gift bag and protection plan to its replacement. Nested add-ons die
   * with the removed parent; linked ones follow the carried id, unless the replacement is no longer
   * a bundle, in which case they are re-nested under it.
   *
   * Best effort on purpose: a duplicate customized line is a double charge at checkout, where a
   * lost add-on is a cheap line the shopper can re-tick, so the removal must run either way. A
   * hang counts, which is why the replay add carries its own deadline.
   * @param {import('@theme/custom.customizer-cart-edit').Cart | null | undefined} cart
   *   - The post-add cart, when the settle carried one; refetched otherwise (the product form
   *   returns null when its own read failed).
   * @param {string} parentKey - The line being replaced.
   * @param {string | null} config - The `_config` the add submitted, which names its line.
   * @param {string | null} rowsName - The new design's unique entry when expansion is on.
   * @returns {Promise<void>}
   */
  async #replayAddOns(cart, parentKey, config, rowsName) {
    try {
      // Some of the product form's add branches dispatch a bare `{}` as their resource, so an
      // items-less payload is treated as no payload rather than as an empty cart.
      const payload = cart?.items?.length ? cart : await fetchCart();
      const old = payload?.items?.find((item) => item.key === parentKey);
      if (!old) return;
      const before = addOnLines(payload, old);
      if (before.native.length === 0 && before.linked.length === 0) return;

      const replacement = replacementLine(payload?.items ?? [], parentKey, config, rowsName);
      if (!replacement) throw new Error('the line that replaced it is not in the cart');
      await carryAddOns(before, replacement, true);
    } catch (error) {
      console.warn(
        '[custom-customizer] could not carry the add-on lines to the edited line',
        error
      );
    }
  }

  /** Close the overlay into the bag: shared tail of the add settle and the in-place update. */
  #handoffToCart() {
    // A committed edit must reset before the next Customize, which starts a new item. Reset on
    // close so an empty Review does not flash during the exit; reset now when already closed.
    const dialog = this.closest('dialog');
    if (!dialog || !dialog.open) this.#buildStore();
    else this.#resetOnClose = true;

    const drawer = this.#cartDrawer;
    // Cart pages have no drawer, so use the stock short exit.
    void this.#overlay?.completeCommit?.(Boolean(drawer));

    // Open a non-auto drawer; its stock open arrives in rAF, so do not key on `dialog.open`.
    // A second showDialog in this tick throws.
    if (drawer && !drawer.hasAttribute('auto-open')) drawer.open?.();
  }

  /**
   * The add failed. Keep the customizer where it was, nothing committed, and put the reason in the
   * footer above the CTA the shopper just pressed.
   * @param {string} [message] - The server's message, when there is one; the footer's own generic
   *   copy is the fallback (a timeout has no message).
   * @param {CartFailure} [reason] - A failure the footer has its own copy for.
   */
  #failCommit(message, reason) {
    this.#endCommit();
    this.#overlay?.cancelCommit?.();

    // A closed overlay leaves a server error to the buy-buttons now back in view. Too large never
    // reaches them and stays true until the photos change, so the next open still shows it.
    const dialog = this.closest('dialog');
    if (!dialog || dialog.open || reason === 'too-large' || reason === 'replacement-incomplete') {
      this.#step?.showCommitError?.(message, reason);
    }
  }

  #endCommit() {
    this.#commitCoordinator.endCommit();
  }

  /**
   * The overlay shell hosting this customizer, if any (a customizer can also render inline).
   * @returns {CustomizerOverlayCommit | null}
   */
  get #overlay() {
    return /** @type {CustomizerOverlayCommit | null} */ (
      this.closest('custom-customizer-overlay')
    );
  }

  /**
   * The hero preview's adapter and engine, lent to the proof pass for its warm caches.
   * @returns {{ adapter?: import('@theme/custom.customizer-adapter').CustomizerAdapter | null,
   *   engine?: import('@theme/custom.customizer-engine').CustomizerEngine | null } | undefined}
   */
  get #previewRenderers() {
    const preview = /** @type {{ renderers?: object } | null} */ (
      this.querySelector('custom-customizer-preview')
    );
    return /** @type {any} */ (preview?.renderers);
  }

  /**
   * The step shell, which owns the footer (CTA + the add-to-cart error line).
   * @returns {CustomizerStepCommit | null}
   */
  get #step() {
    return /** @type {CustomizerStepCommit | null} */ (
      this.querySelector('custom-customizer-step')
    );
  }

  /**
   * The page's cart drawer, when the store renders one (`settings.cart_type`).
   * @returns {(Element & { open?: () => void }) | null}
   */
  get #cartDrawer() {
    return document.querySelector('cart-drawer-component');
  }

  /** Back: a drilled component (`data-drilled` + `toRoot()`) jumps to its ROOT in one step,
   * breadcrumb owns the finer sub-step back. Bound via `custom-customizer/prev`, slash required. */
  prev() {
    const drilled = this.#activeDrill();
    if (drilled) {
      drilled.toRoot();
      return;
    }
    this.store.prev();
  }

  /**
   * The drilled component in the visible step, if any: anything honoring the drill protocol
   * (`data-drilled` while past its root plus a `toRoot()` jump, assets/custom.customizer-drill.js).
   * @returns {ActiveDrill | null}
   */
  #activeDrill() {
    const panel = this.querySelector('[data-customizer-step]:not([hidden])');
    const el = panel?.querySelector('[data-drilled]');
    if (!(el instanceof HTMLElement)) return null;
    const drill = /** @type {{ toRoot?: unknown }} */ (/** @type {unknown} */ (el));
    return typeof drill.toRoot === 'function' ? /** @type {ActiveDrill} */ (drill) : null;
  }

  /** True while a drill's CTA claim is running, see {@link advance}. */
  #claiming = false;

  /**
   * The active drilled view's claim on the footer CTA, when it makes one. ONE reading serves the
   * footer (its label and its disabled state, custom.customizer-step.js) and {@link advance} (what
   * the press does), so what the button says and what it does cannot drift apart.
   * @returns {DrillAdvance | null}
   */
  get drillAdvance() {
    return this.#activeDrill()?.advanceIntent?.() ?? null;
  }

  skip() {
    this.store.skip();
  }

  /**
   * Jump to a step by index. Bound via `on:click="/goTo?index=2"`.
   * @param {{ index?: number }} [data] - Parsed query args from the binding.
   */
  goTo(data) {
    this.store.goTo(Number(data?.index));
  }
}

if (!customElements.get('custom-customizer')) {
  customElements.define('custom-customizer', CustomizerComponent);
}
